// React imports
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useState, useEffect, useMemo, useRef } from 'react';

// Icons
import { BsPersonFill } from 'react-icons/bs';
import { HiMiniUserGroup } from 'react-icons/hi2';

// Data
import { modeData } from '../data/mode.ts';

// Components
import Button from '../components/Button.tsx';

// Contextes
import { useGame } from '../utils/GameContexte.tsx';
import { useInTheGame } from '../utils/InTheGameContexte.tsx';

// Librairies
import gsap from 'gsap';
import { useQuestions } from '../utils/QuestionsContexte.tsx';

function Mode() {
    const navigate = useNavigate();
    const [selectedButtons, setSelectedButtons] = useState<number[]>([]);
    const [questions, setQuestions] = useState<number>(0);
    const totalQuestionsSelected = [5, 10, 15, 20];
    const params = useParams<{ id: string }>();
    const location = useLocation();

    const contentContainer = useRef<HTMLDivElement>(null);

    const { toggleGame } = useInTheGame();
    useEffect(() => {
        toggleGame(true);
    }, [location.pathname]);

    // Mode de jeu
    const result = useMemo(() => {
        return modeData?.find((mode) => mode.key === params.id);
    }, [params.id]);

    //Reiniliaiser le state du jeu
    const {
        setTotalQuestions,
        resetPoints,
        resetQuestionNumber,
        setTotalPlayers,
        setActivePlayer,
        resetEquipesPoints,
        totalPlayers,
    } = useGame();

    const { resetTeamUsedQuestions } = useQuestions();
    useEffect(() => {
        // Réinitialiser les points et le numéro de la question au montage

        resetPoints();
        setTotalQuestions(0); // Optionnel, définissez le total des questions à zéro si nécessaire
        resetQuestionNumber();

        setActivePlayer(0);
        resetEquipesPoints();
        setTotalPlayers(1);

        resetTeamUsedQuestions(0);
        if (totalPlayers > 1) {
            for (let i = 1; i < totalPlayers; i++) {
                resetTeamUsedQuestions(i);
            }
        }
    }, []);

    // Info si c'est entrainement ou pas
    const infoQuestion =
        result?.key === 'entrainement'
            ? 'Vous pouvez choisir entre 5, 10, 15 ou 20 questions : '
            : 'Il faut atteindre 20 points pour remporter la partie.';

    const handleClick = (index: number, number: number) => {
        setSelectedButtons([index]);
        setTotalQuestions(number);
        setQuestions(number);
        resetQuestionNumber();
    };

    // Animation de retour
    const handleBackClick = () => {
        // Empêcher le comportement par défaut si nécessaire, comme la navigation immédiate
        const bannerEl = document.querySelector('.banner');
        const pageEl = document.querySelector('.container');
        const logoNav = document.querySelector('.nav__logo');
        const navEl = document.querySelector('.nav');

        //Assurez-vous que tous les éléments sont présents
        if (bannerEl && pageEl && logoNav && navEl) {
            //Démarrez avec le glissement de la page
            const tl = gsap.timeline({
                onComplete: () => {
                    navigate('/accueil');
                },
            });

            tl.to(pageEl, {
                xPercent: 100,
                opacity: 0,
                duration: 0.5,
                ease: 'power1.in',
            }).to(bannerEl, {
                scaleY: -0,
                duration: 0.5,
                ease: 'power1.out',
            });
        }
    };

    // Handle start du jeu en mode entrainement
    const handleStartClick = () => {
        if (selectedButtons !== null && contentContainer.current) {
            // Sélectionner l'élément à animer
            const content = contentContainer.current;

            // Création de la timeline pour l'animation
            const tl = gsap.timeline({
                onComplete: () => {
                    navigate(`/${params.id}/jeu`);
                },
            });

            tl.to(
                content,
                {
                    scaleY: 0, // Réduit la hauteur à zéro en se rétractant vers le haut
                    transformOrigin: 'top', // Définit le point d'origine de la transformation en bas
                    duration: 0.5,
                    ease: 'power2.inOut',
                }
                /*   "<0.5" */
            );

            // Créer l'animation
            tl.to(content, {
                height: 0, // Réduire la hauteur à 0
                duration: 1, // Durée de l'animation en secondes
                ease: 'power2.inOut', // Type d'accélération pour une transition plus douce
                /*    onComplete: () => {
          console.log("Animation complète");
          // Vous pouvez ici faire d'autres actions post-animation
          navigate(`/${params.id}/jeu`);
        }, */
            });
        }
    };

    // handle solo click
    const handleSoloClick = () => {
        navigate(`/${params.id}/jeu`);
    };

    const buttonsArray = totalQuestionsSelected.map((number, index) => {
        const isSelected = selectedButtons.includes(index);

        return (
            <Button
                scene='difficulty'
                colorName='blue'
                isSelected={isSelected}
                onClick={() => handleClick(index, number)}
                key={`{${index}--difficultySelect}`}
            >
                {number}
            </Button>
        );
    });

    return (
        <>
            <div
                className='mode content-to-collapse'
                ref={contentContainer}
            >
                <p className='modeTexte text-page-semib animation-item'>{result?.text1 ?? ''}</p>
                <div>
                    <p className='modeTexte modeTexte--bold animation-item'>{infoQuestion ?? ''}</p>

                    <div className='modeButtons animation-item'>
                        {result?.key === 'entrainement' && <>{buttonsArray}</>}
                    </div>
                </div>
                <p className='modeTexte modeTexte--italic animation-item'>{result?.text2 ?? ''}</p>

                {result?.key !== 'entrainement' && (
                    <div className='modeButtons animation-item'>
                        <Button
                            scene='mode'
                            colorName='blue'
                            isSelected={false}
                            onClick={handleSoloClick}
                            id='modal_seg2_maybe'
                            label='Commencez-2'
                        >
                            <BsPersonFill />
                            Solo
                        </Button>

                        <Link to={`/${params.id}/multi`}>
                            <Button
                                scene='mode'
                                colorName='red'
                                isSelected={false}
                                id='modal_seg3_maybe'
                                label='Commencez-3'
                            >
                                <HiMiniUserGroup />
                                Multijoueur
                            </Button>
                        </Link>
                    </div>
                )}

                {result?.key === 'entrainement' && (
                    <Button
                        scene='menu'
                        colorName='red'
                        isSelected={false}
                        disabled={questions === 0}
                        onClick={handleStartClick}
                        animationClass='animation-item'
                        id='modal_seg1_maybe'
                        label='Commencez-1'
                    >
                        Commencer
                    </Button>
                )}

                <Button
                    scene='menu'
                    colorName='blue'
                    isSelected={false}
                    onClick={handleBackClick}
                    animationClass='animation-item'
                >
                    Retour aux modes de jeu
                </Button>
            </div>
        </>
    );
}

export default Mode;
